export interface CalculatorValues {
  monthlySavings: number;
  startingAmount: number;
  savingsPeriod: number;
  expectedReturn: number;
  currency: string;
}

export interface ChartDataPoint {
  year: string;
  'Sparande': number;
  'Totalt': number;
  'Avkastning': number;
}

// Function to create translated chart data
export function createTranslatedChartData(chartData: ChartDataPoint[], translations: { chartData: { savings: string; total: string; return: string } }): ChartDataPoint[] {
  return chartData.map(dataPoint => ({
    ...dataPoint,
    [translations.chartData.savings]: dataPoint['Sparande'],
    [translations.chartData.total]: dataPoint['Totalt'],
    [translations.chartData.return]: dataPoint['Avkastning']
  }));
}

export interface RiskPreset {
  name: string;
  summary: string;
  description: string;
  expected_return: number;
  weights: Record<string, number>;
}

export interface RiskColors {
  color: string;
  bgColor: string;
  borderColor: string;
}

// Utility function to get default currency based on locale
export function getDefaultCurrency(): string {
  // Try both environment variables - server and client
  const locale = process.env.WEBSITE_LOCALE || process.env.NEXT_PUBLIC_WEBSITE_LOCALE || 'sv-SE';
  
  
  // English locales
  if (locale === 'en-GB') {
    return '£'; // GBP for British English
  } else if (locale === 'en-US') {
    return '$'; // USD for American English
  }
  
  // Nordic locales
  else if (locale.startsWith('sv')) {
    return 'kr'; // SEK for Swedish
  } else if (locale.startsWith('nb') || locale.startsWith('nn')) {
    return 'kr'; // NOK for Norwegian
  } else if (locale.startsWith('da')) {
    return 'kr'; // DKK for Danish
  }
  
  // European locales
  else if (locale.startsWith('de')) {
    return '€'; // EUR for German
  } else if (locale.startsWith('fr')) {
    return '€'; // EUR for French
  } else if (locale.startsWith('es')) {
    return '€'; // EUR for Spanish
  } else if (locale.startsWith('it')) {
    return '€'; // EUR for Italian
  } else if (locale.startsWith('nl')) {
    return '€'; // EUR for Dutch
  } else if (locale.startsWith('pt')) {
    return '€'; // EUR for Portuguese
  } else if (locale.startsWith('fi')) {
    return '€'; // EUR for Finnish
  } else if (locale.startsWith('pl')) {
    return '€'; // EUR for Polish
  }
  
  // Swiss locale
  else if (locale.startsWith('de-CH') || locale.startsWith('fr-CH') || locale.startsWith('it-CH')) {
    return 'CHF'; // CHF for Swiss locales
  }
  
  return 'kr'; // Default to SEK
}

// Asset allocation data object with translation support
export const ASSET_ALLOCATION_DATA = {
  // Antaganden för "expected_return" (kan räknas dynamiskt):
  assumptions: {
    savings_account: 0.02,
    bonds: 0.03,               // kort duration/IG
    equities_stable: 0.075,
    equities_growth: 0.09,
    alts_private_crypto: 0.12
  },

  presets: {
    low: {
      expected_return: 0.0415,   // 20%*0.02 + 50%*0.03 + 30%*0.075
      weights: {
        savings_account: 0.20,
        bonds: 0.50,
        equities_stable: 0.30,
        equities_growth: 0.00,
        equities_dividend: 0.00,
        real_estate: 0.00,
        safe_havens: 0.00,
        alts_private_crypto: 0.00
      }
    },
    medium: {
      expected_return: 0.06275,  // 10%*0.02 + 20%*0.03 + 55%*0.075 + 15%*0.09
      weights: {
        savings_account: 0.10,
        bonds: 0.20,
        equities_stable: 0.55,
        equities_growth: 0.15,
        equities_dividend: 0.00,
        real_estate: 0.00,
        safe_havens: 0.00,
        alts_private_crypto: 0.00
      }
    },
    high: {
      expected_return: 0.07375,  // 5%*0.02 + 10%*0.03 + 45%*0.075 + 40%*0.09
      weights: {
        savings_account: 0.05,
        bonds: 0.10,
        equities_stable: 0.45,
        equities_growth: 0.40,
        equities_dividend: 0.00,
        real_estate: 0.00,
        safe_havens: 0.00,
        alts_private_crypto: 0.00
      }
    },
    very_high: {
      expected_return: 0.08275,  // 5%*0.02 + 35%*0.075 + 55%*0.09 + 5%*0.12
      weights: {
        savings_account: 0.05,
        bonds: 0.00,
        equities_stable: 0.35,
        equities_growth: 0.55,
        equities_dividend: 0.00,
        real_estate: 0.00,
        safe_havens: 0.00,
        alts_private_crypto: 0.05  // valfri krydda; sätt 0 om du vill hålla det superenkelt
      }
    }
  }
};

// Function to create translated asset allocation data
export function createTranslatedAssetAllocationData(translations: { 
  assetAllocation: { 
    labels: Record<string, string>; 
    presets: Record<string, { name: string; summary: string; description: string }> 
  } 
}) {
  return {
    labels: translations.assetAllocation.labels,
    assumptions: ASSET_ALLOCATION_DATA.assumptions,
    presets: {
      low: {
        ...ASSET_ALLOCATION_DATA.presets.low,
        name: translations.assetAllocation.presets.low.name,
        summary: translations.assetAllocation.presets.low.summary,
        description: translations.assetAllocation.presets.low.description,
      },
      medium: {
        ...ASSET_ALLOCATION_DATA.presets.medium,
        name: translations.assetAllocation.presets.medium.name,
        summary: translations.assetAllocation.presets.medium.summary,
        description: translations.assetAllocation.presets.medium.description,
      },
      high: {
        ...ASSET_ALLOCATION_DATA.presets.high,
        name: translations.assetAllocation.presets.high.name,
        summary: translations.assetAllocation.presets.high.summary,
        description: translations.assetAllocation.presets.high.description,
      },
      very_high: {
        ...ASSET_ALLOCATION_DATA.presets.very_high,
        name: translations.assetAllocation.presets.very_high.name,
        summary: translations.assetAllocation.presets.very_high.summary,
        description: translations.assetAllocation.presets.very_high.description,
      }
    }
  };
}
